import {
  type DefaultError,
  type Mutation,
  type Query,
  MutationCache,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query';

import { AuthCancelledError } from '@/features/auth/components/GlobalAuthModal/state';
import { ApiError } from '@/lib/api/error';
import { ForbiddenApiError } from '@/lib/api/error.forbidden';
import { NoOrganizationAccessError } from '@/lib/api/error.no-organization-access';
import { PersonalEmailLoginDeniedError } from '@/lib/api/error.personal-email-login-denied';
import { PersonalEmailNotAllowedError } from '@/lib/api/error.personal-email-not-allowed';
import { ReauthRequiredError } from '@/lib/api/error.reauth-required';
import { UnauthorizedApiError } from '@/lib/api/error.unauthorized';
import {
  convertOrpcErrorToErrorResponse,
  isOrpcError,
} from '@/lib/api/orpc/error-adapter';
import { AUTH_EVENTS, ERROR_EVENTS } from '@/lib/events/constants';
import { emitEvent } from '@/lib/events/helpers';
import { getLogger } from '@/lib/logger';

/**
 * @see https://tanstack.com/query/latest/docs/framework/react/typescript#typing-meta
 */
interface ReactQueryMeta extends Record<string, unknown> {
  /**
   * クエリのエラー時にグローバルエラーダイアログを表示しない
   */
  disableGlobalErrorHandling?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: ReactQueryMeta;
    mutationMeta: ReactQueryMeta;
  }
}

/**
 * 無限スクロールのクエリを識別するための一意な識別子。
 * queryKey にセットすることで、無限スクロールのクエリであることを示す。
 * 他のクエリキーと衝突しないように、ユニークな識別子を使用する。
 */
export const INFINITE_QUERY_KEY_SUFFIX = `infinite-query-${crypto.randomUUID()}`;

export const handleSystemApiError = (props: {
  error: Error | undefined;
  isHistoryBackIfPossible?: boolean;
}) => {
  const { error, isHistoryBackIfPossible = false } = props;

  if (error instanceof NoOrganizationAccessError) {
    emitEvent(AUTH_EVENTS.NO_ORGANIZATION_ACCESS);
    return;
  }
  if (
    error instanceof PersonalEmailNotAllowedError ||
    error instanceof PersonalEmailLoginDeniedError
  ) {
    // 個人メアドに起因する再ログイン必須エラー。組織切替では復旧できないため再ログイン導線を表示する。
    emitEvent(ERROR_EVENTS.GLOBAL, {
      description: error.displayMessage,
      actionType: 'reSignIn',
      action: 'show',
    });
    return;
  }
  if (error instanceof AuthCancelledError) {
    // ユーザーが再認証モーダルを明示的にキャンセルした。意図的な中断のため
    // 汎用エラーダイアログは表示しない（ログアウトもしない）。
    return;
  }
  if (error instanceof ReauthRequiredError) {
    // 再認証が必要（未ラップ、または再認証後も解消せず）。サーバー文言を表示する。
    // ラップ済みの機密操作は handleApiErrorWithReauth が再認証→再実行を担う。
    emitEvent(ERROR_EVENTS.GLOBAL, {
      description: error.displayMessage,
      isHistoryBackIfPossible,
      action: 'show',
    });
    return;
  }
  if (error instanceof ForbiddenApiError) {
    // イベントベースでForbiddenApiErrorDialogを表示
    emitEvent(ERROR_EVENTS.FORBIDDEN_API, {
      accessibles: error.accessibles,
      action: 'show',
    });
    return;
  }
  if (error instanceof UnauthorizedApiError) {
    // ログアウトされるのでエラーダイアログを表示しない
    return;
  }
  if (error instanceof ApiError) {
    // イベントベースでGlobalErrorDialogを表示
    emitEvent(ERROR_EVENTS.GLOBAL, {
      description: error.getResponse().displayMessage,
      isHistoryBackIfPossible,
      action: 'show',
    });
    return;
  }
  if (isOrpcError(error)) {
    const response = convertOrpcErrorToErrorResponse(error);
    emitEvent(ERROR_EVENTS.GLOBAL, {
      description: response.displayMessage,
      isHistoryBackIfPossible,
      action: 'show',
    });
    return;
  }

  // イベントベースでGlobalErrorDialogを表示（汎用エラー）
  emitEvent(ERROR_EVENTS.GLOBAL, {
    isHistoryBackIfPossible,
    action: 'show',
  });
};

/**
 * Mutationのエラーハンドリング関数
 * @note defaultOptions.mutations.onError では mutation オブジェクトが渡されないため、
 *       QueryCache と同様に MutationCache.onError で登録する。
 */
const defaultMutationOnError = (
  error: DefaultError,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  getLogger().error('Error in mutation', error);
  if (mutation.meta?.disableGlobalErrorHandling) {
    return;
  }
  return handleSystemApiError({ error });
};

/**
 * Queryのエラーハンドリング関数
 */
const defaultQueryOnError = (
  error: DefaultError,
  query: Query<unknown, unknown, unknown>,
) => {
  getLogger().error('Error in query cache', { error, query });
  // metaでグローバルエラーハンドリングを無効化している場合はスキップ
  if (query.meta?.disableGlobalErrorHandling) {
    return;
  }
  return handleSystemApiError({ error });
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof NoOrganizationAccessError) {
          return false;
        }
        // 401, 403はリトライしない
        if (
          error instanceof UnauthorizedApiError ||
          error instanceof ForbiddenApiError ||
          error instanceof PersonalEmailNotAllowedError ||
          error instanceof PersonalEmailLoginDeniedError ||
          error instanceof ReauthRequiredError ||
          error instanceof AuthCancelledError
        ) {
          return false;
        }
        // 400系エラー（クライアントエラー）はリトライしない
        if (error instanceof ApiError) {
          const statusCode = error.getResponse().statusCode;
          if (statusCode >= 400 && statusCode < 500) {
            return false;
          }
        }
        if (isOrpcError(error) && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: defaultMutationOnError,
  }),
  queryCache: new QueryCache({
    /**
     * useQuery()のオプションにはonErrorが存在しないため、`defaultOptions.queries.onError`は使用できない
     * @see https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
     */
    onError: defaultQueryOnError,
  }),
});

/**
 * Mutationのエラーハンドリング関数
 * @note {@link MutateOptions.onError}から引用
 */
type OnErrorFn<TError, TVariables, TContext> = (
  error: TError,
  variables: TVariables,
  context: TContext | undefined,
) => unknown;

/**
 * `onError`で発生したエラーをグローバルエラーハンドリングに委譲する
 * @note {@link MutateOptions.onError}から引用
 */
export function withGlobalErrorHandling<
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  fn: OnErrorFn<TError, TVariables, TContext>,
): OnErrorFn<TError, TVariables, TContext> {
  return (error, variables, context) => {
    try {
      return fn(error, variables, context);
    } catch (caughtError) {
      handleSystemApiError({
        error: caughtError instanceof Error ? caughtError : undefined,
      });
      throw caughtError;
    }
  };
}
